/**
 * Generate and download booking receipt as PDF
 * Uses jsPDF for client-side PDF generation
 */
import { saveAs } from 'file-saver';

const formatDate = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  return date.toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
};

const formatCurrency = (amount, currency = 'INR') => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount);
};

/**
 * Normalize booking object from different sources (TripsPage, BookingDetailsPage, BookingConfirmationPage)
 * Exported for ReceiptPreviewModal
 */
export const getReceiptData = (booking) => {
  if (!booking) return null;
  const prop = booking.property || {};
  const guest = booking.guestInfo || {};
  const formData = booking.formData || {};
  const checkIn = booking.checkIn || booking.checkInDate;
  const checkOut = booking.checkOut || booking.checkOutDate;
  const start = checkIn ? new Date(checkIn) : null;
  const end = checkOut ? new Date(checkOut) : null;
  const nights = start && end ? Math.ceil((end - start) / (1000 * 60 * 60 * 24)) : 0;
  const guestsNum = typeof booking.guests === 'number'
    ? booking.guests
    : (booking.guests?.adults ?? booking.guestsDetail?.adults ?? 1);
  let guestName = 'Guest';
  if (guest.firstName && guest.lastName) guestName = `${guest.firstName} ${guest.lastName}`.trim();
  else if (guest.firstName || guest.name) guestName = guest.firstName || guest.name || 'Guest';
  else if (formData.firstName || formData.lastName) guestName = `${formData.firstName || ''} ${formData.lastName || ''}`.trim() || 'Guest';
  else if (formData.name) guestName = formData.name;
  const paymentMethod = booking.paymentMethod || 'payAtHotel';
  const paymentStatus = booking.paymentStatus || null;
  const paymentLabel = paymentMethod === 'online' && paymentStatus === 'paid'
    ? 'Paid online'
    : 'Pay at hotel at check-in';

  return {
    bookingId: booking.bookingId || booking._id || 'N/A',
    status: booking.status || 'pending',
    propertyName: prop.name || 'Property',
    propertyCity: prop.city || '',
    propertyState: prop.state || 'India',
    checkIn,
    checkOut,
    nights,
    guests: guestsNum,
    totalPrice: booking.totalPrice || 0,
    currency: booking.currency || 'INR',
    guestName: guestName || 'Guest',
    guestEmail: guest.email || formData.email || '',
    guestPhone: guest.phone || formData.phone || '',
    paymentMethod,
    paymentStatus,
    paymentLabel
  };
};

/**
 * Generate PDF receipt using jsPDF (if available) or HTML fallback
 */
export const downloadBookingReceipt = async (booking) => {
  const b = getReceiptData(booking);
  if (!b) return;

  try {
    const mod = await import('jspdf');
    const jsPDF = mod.jsPDF || mod.default;
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    let y = 20;

    // Title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Booking Receipt', pageW / 2, y, { align: 'center' });
    y += 12;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated: ${formatDate(new Date())}`, pageW / 2, y, { align: 'center' });
    y += 15;

    // Line
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, pageW - 20, y);
    y += 10;

    // Booking ID & Status
    doc.setFont(undefined, 'bold');
    doc.text(`Booking ID: #${b.bookingId}`, 20, y);
    doc.setFont(undefined, 'normal');
    doc.text(`Status: ${b.status}`, pageW - 20, y, { align: 'right' });
    y += 10;

    // Property
    doc.setFont(undefined, 'bold');
    doc.text('Property', 20, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.text(b.propertyName, 20, y);
    y += 5;
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`${b.propertyCity}${b.propertyCity && b.propertyState ? ', ' : ''}${b.propertyState}`, 20, y);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    y += 12;

    // Dates
    doc.setFont(undefined, 'bold');
    doc.text('Check-in', 20, y);
    doc.text('Check-out', 110, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.text(formatDate(b.checkIn), 20, y);
    doc.text(formatDate(b.checkOut), 110, y);
    y += 6;
    doc.text(`Nights: ${b.nights}`, 20, y);
    doc.text(`Guests: ${b.guests}`, 110, y);
    y += 12;

    // Guest
    doc.setFont(undefined, 'bold');
    doc.text('Guest', 20, y);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.text(b.guestName, 20, y);
    if (b.guestEmail) {
      y += 5;
      doc.text(b.guestEmail, 20, y);
    }
    if (b.guestPhone) {
      y += 5;
      doc.text(b.guestPhone, 20, y);
    }
    y += 12;

    // Total - app primary color accent
    doc.setDrawColor(232, 232, 232);
    doc.line(20, y, pageW - 20, y);
    y += 8;
    doc.setFont(undefined, 'bold');
    doc.text('Total Amount', 20, y);
    doc.setTextColor(255, 56, 92); // --primary-color #FF385C
    doc.text(formatCurrency(b.totalPrice, b.currency), pageW - 20, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 6;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(`Payment: ${b.paymentLabel || 'Pay at hotel at check-in'}`, 20, y);
    y += 10;

    doc.setFont(undefined, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text('Thank you for booking with us. For support, contact the host or property.', 20, y);
    doc.setTextColor(0, 0, 0);

    const filename = `Booking-Receipt-${b.bookingId}-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
  } catch (err) {
    console.error('PDF generation failed, falling back to HTML:', err);
    downloadReceiptAsHtml(b);
  }
};

/**
 * Fallback: download as HTML if jsPDF fails
 */
const downloadReceiptAsHtml = (b) => {
  const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><title>Booking Receipt #${b.bookingId}</title>
<style>
:root{--primary:#FF385C;--text-dark:#222;--text-light:#717171;--border:#E8E8E8}
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:520px;margin:40px auto;padding:28px;color:var(--text-dark);line-height:1.5}
h1{font-family:'Playfair Display',Georgia,serif;text-align:center;font-size:1.5rem;color:var(--text-dark);border-bottom:2px solid var(--primary);padding-bottom:10px;margin-bottom:8px}
.receipt-gen{text-align:center;font-size:0.875rem;color:var(--text-light);margin-bottom:16px}
.receipt-divider{height:1px;background:var(--border);margin:16px 0}
.receipt-section{margin:18px 0}
.receipt-section h3{font-size:0.6875rem;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-light);margin-bottom:6px}
.receipt-row{display:flex;justify-content:space-between;padding:6px 0;font-size:0.875rem}
.receipt-total{font-size:1.125rem;font-weight:700;margin-top:20px;padding-top:16px;border-top:2px solid var(--border)}
.receipt-total .amount{color:var(--primary)}
.footer{margin-top:24px;font-size:0.75rem;color:var(--text-light);text-align:center}
</style>
</head>
<body>
<h1>Booking Receipt</h1>
<p class="receipt-gen">Generated: ${formatDate(new Date())}</p>
<div class="receipt-divider"></div>
<div class="receipt-section">
  <h3>Booking Details</h3>
  <div class="receipt-row"><span>Booking ID</span><span>#${b.bookingId}</span></div>
  <div class="receipt-row"><span>Status</span><span>${b.status}</span></div>
</div>
<div class="receipt-section">
  <h3>Property</h3>
  <div class="receipt-row"><span>Name</span><span>${b.propertyName}</span></div>
  <div class="receipt-row"><span>Location</span><span>${b.propertyCity}, ${b.propertyState}</span></div>
</div>
<div class="receipt-section">
  <h3>Dates</h3>
  <div class="receipt-row"><span>Check-in</span><span>${formatDate(b.checkIn)}</span></div>
  <div class="receipt-row"><span>Check-out</span><span>${formatDate(b.checkOut)}</span></div>
  <div class="receipt-row"><span>Nights</span><span>${b.nights}</span></div>
  <div class="receipt-row"><span>Guests</span><span>${b.guests}</span></div>
</div>
<div class="receipt-section">
  <h3>Guest</h3>
  <div class="receipt-row"><span>Name</span><span>${b.guestName}</span></div>
  ${b.guestEmail ? `<div class="receipt-row"><span>Email</span><span>${b.guestEmail}</span></div>` : ''}
  ${b.guestPhone ? `<div class="receipt-row"><span>Phone</span><span>${b.guestPhone}</span></div>` : ''}
</div>
<div class="receipt-divider"></div>
<div class="receipt-section receipt-total">
  <div class="receipt-row"><span>Total Amount</span><span class="amount">${formatCurrency(b.totalPrice, b.currency)}</span></div>
</div>
<p class="footer">Thank you for booking with us. Use Print (Ctrl+P) → Save as PDF to save as PDF.</p>
</body>
</html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  saveAs(blob, `Booking-Receipt-${b.bookingId}-${new Date().toISOString().slice(0, 10)}.html`);
};
